import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that lets a shopper pick a bundle quantity (1, 2, or 3) and
 * keeps the shared `quantity` input on the product form in sync, whether or
 * not a regular quantity stepper block is also present.
 *
 * @extends {Component}
 */
class StackingDiscountSelector extends Component {
  /** @type {HTMLInputElement | undefined} */
  #createdQuantityInput;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
    this.#applyQuantity(this.#selectedQuantity);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
  }

  /**
   * Handles a bundle option being selected.
   * @param {Event} event
   */
  selectOption(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    this.#applyQuantity(Number(event.target.value));
  }

  /**
   * Keeps the selected option in sync when the quantity changes elsewhere,
   * e.g. from a quantity stepper block also present on the form.
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.target !== this.#quantityInput) return;

    const option = this.querySelector(`.stacking-discount-option__input[value="${event.detail.quantity}"]`);

    if (option instanceof HTMLInputElement) option.checked = true;
  };

  get #selectedQuantity() {
    const checked = this.querySelector('.stacking-discount-option__input:checked');

    return checked instanceof HTMLInputElement ? Number(checked.value) : 1;
  }

  get #quantityInput() {
    const form = this.closest('form');

    if (!form) return null;

    const existingInput = form.querySelector('[name="quantity"]');

    if (existingInput instanceof HTMLInputElement) return existingInput;

    if (this.#createdQuantityInput?.isConnected) return this.#createdQuantityInput;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'quantity';
    form.append(input);
    this.#createdQuantityInput = input;

    return input;
  }

  /**
   * @param {number} quantity
   */
  #applyQuantity(quantity) {
    const input = this.#quantityInput;

    if (!input || Number(input.value) === quantity) return;

    input.value = String(quantity);
    input.dispatchEvent(new QuantitySelectorUpdateEvent(quantity));
  }
}

if (!customElements.get('stacking-discount-selector')) {
  customElements.define('stacking-discount-selector', StackingDiscountSelector);
}
